import React, { useState } from "react";
import { useTransition, animated } from "react-spring";
import { TfiClose } from "react-icons/tfi";
import {
  Facebook_Share,
  Email_Share,
  X_Share,
  Linkdin_Share
} from "../../../../assets/icons";
import useAppAccessor from "../../../../hooks/useappAccessor";
import { toast } from "react-toastify";
import { getBrandColor } from "../../../../utils/brand.util";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share";

const CustomShareBottomSheet = ({ isOpen, toggleSheet, linkUrl }: any) => {
  const { getUserData } = useAppAccessor();
  const { language, client } = getUserData();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  const transitions = useTransition(isOpen, {
    from: { opacity: 0, transform: "translateY(10%)" },
    enter: { opacity: 1, transform: "translateY(0%)" },
    leave: { opacity: 0, transform: "translateY(10%)" },
    config: { tension: 300, friction: 30 }
  });
  const title = "Join my live stream!";

  const handleCopy = () => {
    navigator.clipboard.writeText(linkUrl);
    toggleSheet();

    toast(language['link_copied'], {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  };

  const handleEmailShare = () => {
    const emailSubject = encodeURIComponent(title);
    const emailBody = encodeURIComponent(linkUrl);

    const mailtoLink = `mailto:?subject=${emailSubject}&body=${emailBody}`;
    const windowWidth = 500;
    const windowHeight = 500;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    const leftPosition = (screenWidth - windowWidth) / 2;
    const topPosition = (screenHeight - windowHeight) / 2;

    window.open(
      mailtoLink,
      "Share using Email",
      `width=${windowWidth},height=${windowHeight},left=${leftPosition},top=${topPosition},resizable=yes`
    );
  };

  const brandColor = getBrandColor(client);

  const truncateLink = (url: string, maxLength: number = 35) => {
    if (url.length <= maxLength) {
      return url;
    }
    return url.substring(0, maxLength) + "...";
  };

  return transitions((styles: any, item: any) =>
    item ? (
      <animated.div style={{ ...styles }} className="flex justify-center">
        <div
          className="flex flex-col pointer-events-auto rounded-t-3xl rounded-b-3xl"
          style={{
            backgroundColor: "rgb(42, 42, 42)",
            boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.5), 0 -2px 8px rgba(0, 0, 0, 0.3)",
            borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
            width: "100%",
            maxWidth: "480px",
          }}
        >
          <div className="flex w-full justify-between items-center px-5 pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div 
                className="h-1 w-12 rounded-full"
                style={{ backgroundColor: brandColor }}
              />
              <h2 className="text-white text-xl font-semibold tracking-tight">
                {language["share_button"]}
              </h2>
            </div>
            <button
              onClick={toggleSheet}
              className="p-2 rounded-full hover:bg-white/10 active:bg-white/20 transition-colors duration-200"
              aria-label="Close"
            >
              <TfiClose
                color="#DEDEDE"
                size={20}
                className="cursor-pointer"
              />
            </button>
          </div>

          <div className="flex w-full justify-center px-5 pb-6 gap-6">
            <div 
              className="flex flex-col items-center gap-2 cursor-pointer group"
              onMouseEnter={() => setHoveredIndex(0)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <FacebookShareButton 
                url={linkUrl} 
                title={title}
                className="transition-transform duration-200 group-hover:scale-110"
              >
                <div 
                  className="p-3 rounded-full transition-all duration-200"
                  style={{
                    backgroundColor: hoveredIndex === 0 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    boxShadow: hoveredIndex === 0 ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none'
                  }}
                >
                  <img
                    style={{ height: 40, width: 40 }}
                    src={Facebook_Share}
                    alt="Facebook"
                  />
                </div>
              </FacebookShareButton>
              <span className="text-white text-xs font-medium mt-1">Facebook</span>
            </div>
            
            <div 
              className="flex flex-col items-center gap-2 cursor-pointer group"
              onClick={handleEmailShare}
              onMouseEnter={() => setHoveredIndex(1)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div 
                className="p-3 rounded-full transition-all duration-200 group-hover:scale-110"
                style={{
                  backgroundColor: hoveredIndex === 1 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                  boxShadow: hoveredIndex === 1 ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none'
                }}
              >
                <img 
                  style={{ height: 40, width: 40 }} 
                  src={Email_Share}
                  alt="Email"
                />
              </div>
              <span className="text-white text-xs font-medium mt-1">{language['email_field']}</span>
            </div>
            
            <div 
              className="flex flex-col items-center gap-2 cursor-pointer group"
              onMouseEnter={() => setHoveredIndex(2)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <TwitterShareButton 
                url={linkUrl} 
                title={title}
                className="transition-transform duration-200 group-hover:scale-110"
              >
                <div 
                  className="p-3 rounded-full transition-all duration-200"
                  style={{
                    backgroundColor: hoveredIndex === 2 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    boxShadow: hoveredIndex === 2 ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none'
                  }}
                >
                  <img 
                    style={{ height: 40, width: 40 }} 
                    src={X_Share}
                    alt="X"
                  />
                </div>
              </TwitterShareButton>
              <span className="text-white text-xs font-medium mt-1">X</span>
            </div>
            
            <div 
              className="flex flex-col items-center gap-2 cursor-pointer group"
              onMouseEnter={() => setHoveredIndex(3)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <LinkedinShareButton 
                url={linkUrl} 
                title={title}
                className="transition-transform duration-200 group-hover:scale-110"
              >
                <div 
                  className="p-3 rounded-full transition-all duration-200"
                  style={{
                    backgroundColor: hoveredIndex === 3 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    boxShadow: hoveredIndex === 3 ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none'
                  }}
                >
                  <img
                    style={{ height: 40, width: 40 }}
                    src={Linkdin_Share}
                    alt="LinkedIn"
                  />
                </div>
              </LinkedinShareButton>
              <span className="text-white text-xs font-medium mt-1">LinkedIn</span>
            </div>
          </div>

          <div className="px-5 pb-6">
            <div className="flex w-full rounded-xl overflow-hidden mx-auto" style={{
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              maxWidth: "420px"
            }}>
              <div
                className="flex-1 h-14 px-4 flex items-center bg-[#1a1a1a] border-r border-white/10"
              >
                <p className="text-white text-sm font-medium w-full truncate" title={linkUrl}>
                  {truncateLink(linkUrl)}
                </p>
              </div>
              <button
                onClick={handleCopy}
                className="px-6 h-14 flex items-center justify-center cursor-pointer transition-all duration-200 active:scale-95 whitespace-nowrap"
                style={{
                  backgroundColor: brandColor,
                  minWidth: "110px"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                <span className="text-white text-sm font-semibold">
                  {language["copy_button"]}
                </span>
              </button>
            </div>
          </div>
        </div>
      </animated.div>
    ) : null
  );
};

export default CustomShareBottomSheet;
